import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import DOMPurify from "dompurify";
import Layout from "components/yenimahalle/Layout";
import ContentHeader from "components/yenimahalle/ContentHeader";
import PortalApi from "../../api/PortalApi";
import { GetContentResponse } from "../../models/Response/Portal";

const Container = styled.div.attrs({ className: "container mt-5" })``;
const CardContainer = styled.div.attrs({ className: "card" })``;
const CardBody = styled.div.attrs({ className: "card-body" })`
  /* allow horizontal scroll when table overflows */
  overflow-x: auto;

  /* force any table inside to fill width and fix layout */
  & table {
    width: 100%;
    table-layout: fixed;
  }

  /* wrap long words/cells onto new lines */
  & table th,
  & table td {
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
  }
`;

function sanitizeHtmlWithFixedImg(html: string) {
  let fixedHtml = html.replace(/\n/g, "<br />");

  fixedHtml = fixedHtml.replace(/(src|href)="\/([^"]+)"/g, (_, attr, path) => {
    const fullPath = `${path}`;
    return `${attr}="${fullPath}"`;
  });

  const clean = DOMPurify.sanitize(fixedHtml, {
    ALLOWED_TAGS: [
      "iframe",
      "embed",
      "object",
      "video",
      "source",
      "picture",
      "img",
      "a",
      "i",
      "hr",
      "br",
      "strong",
      "p",
      "b",
      "div",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
    ],
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "class",
      "style",
      "src",
      "srcdoc",
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "data",
      "type",
      "width",
      "height",
      "alt",
      "title",
      "loading",
      "poster",
      "colspan",
      "rowspan",
      "align",
      "valign", // 👈 bazı table-related attributeler
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp):|mailto:|tel:|data:image\/|\/\/)/i,
  });

  const fixed = clean.replace(
    /<a\s+(?![^>]*target=)([^>]*)href="([^"]+)"([^>]*)>/g,
    `<a $1 href="$2"$3 target="_blank" rel="noopener noreferrer">`,
  );

  return fixed;
}

const Content: React.FC = () => {
  const { contentID } = useParams<{ contentID: string }>();
  const [content, setContent] = useState<GetContentResponse | null>(null);

  useEffect(() => {
    if (contentID) {
      PortalApi.getContent(Number(contentID)).then((response) => {
        if (response) setContent(response);
      });
    }
  }, [contentID]);

  if (!content) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <Layout>
      <ContentHeader
        headingLine1={content.header}
        headingLine2={content.mainHeader}
        bottomNotification={content.subHeader}
        photoUrl={content.secondImage}
      />
      <Container>
        <CardContainer>
          <CardBody>
            {content.contentLinesList.map((line, rowIndex) => (
              <div className="row mb-3" key={rowIndex}>
                {line.contentCellList.map((cell, colIndex) => (
                  <div className={`col-md-${cell.width}`} key={colIndex}>
                    {cell.contentType === 1 && <h2 className="fw-bold">{cell.content}</h2>}
                    {cell.contentType === 2 && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtmlWithFixedImg(cell.content || ""),
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </CardBody>
        </CardContainer>
      </Container>
    </Layout>
  );
};

export default Content;
